import { DEFAULT_OPACITY } from "constants/chart.constants";
import { SeriesOption } from "echarts";
import { SeriesType } from "types/echarts";
import { EvaChartStates } from "types/factory";

import {
  BLUR_OPACITY,
  DEFAULT_Z_INDEX,
  HIGHLIGHTED_Z_INDEX,
  LINE_OF_BEST_FIT_LABEL,
  LINE_OF_BEST_FIT_Z_INDEX,
  POINT_BLUR_OPACITY,
  TYPEWELL_Z_INDEX
} from "../../translators/constants";
import { computeScatterSelectionStates } from "../../translators/utils";

interface SeriesHighlightUpdate {
  id: string;
  itemStyle: {
    opacity: number;
  };
  symbolSize: number;
  z: number;
  lineStyle?: {
    width: number;
    opacity: number;
  };
}

export function crossPlotHighlightedSeries(states: Partial<EvaChartStates>) {
  const {
    series,
    hoverLegendItem,
    hoverLegendId,
    selectedGroups = [],
    syncWells,
    getPointStyle,
    getSeriesStyle,
    getHighlightedSeriesStyle,
    chartOptions
  } = states;

  const isOpaque = !(hoverLegendItem || selectedGroups.length > 0);
  const { opacity: pointOpacityValue, size: pointSizeValue } = getPointStyle();
  const highlightSyncedWell =
    !hoverLegendItem && syncWells?.isSync && syncWells?.activeWells?.length > 0;

  const highlightedSeries = series.map((s, index) => {
    const { label, mode } = s;

    const type = mode === "markers" ? SeriesType.Scatter : mode;
    const isLine = type === SeriesType.Line;
    const { isHovered } = computeScatterSelectionStates({
      hoverLegendItem,
      label,
      selectedGroups
    });

    if (highlightSyncedWell) {
      return {
        id: `${label}, ${index}`,
        itemStyle: {
          opacity: BLUR_OPACITY
        },
        symbolSize: pointSizeValue,
        z: isHovered ? HIGHLIGHTED_Z_INDEX : DEFAULT_Z_INDEX
      };
    }

    const baseUpdate: SeriesHighlightUpdate = {
      id: `${label}, ${index}`,
      itemStyle: {
        opacity: isOpaque || isHovered ? pointOpacityValue : POINT_BLUR_OPACITY
      },
      symbolSize: type === SeriesType.Scatter ? pointSizeValue : 0.2,
      z: isHovered
        ? HIGHLIGHTED_Z_INDEX
        : isLine
        ? LINE_OF_BEST_FIT_Z_INDEX
        : DEFAULT_Z_INDEX
    };

    // Handle line of best fit styling (both hovered and non-hovered states)
    if (isLine && label.includes(LINE_OF_BEST_FIT_LABEL)) {
      const highlightedStyle = getHighlightedSeriesStyle(label);
      const currentStyle = getSeriesStyle?.(label);
      baseUpdate.lineStyle = {
        width: isHovered ? highlightedStyle.thickness : currentStyle?.thickness,
        opacity: isHovered ? highlightedStyle.opacity : currentStyle?.opacity
      };
    }

    return baseUpdate;
  });

  // Preserve typewell series from chartOptions - they are not in the API series
  // but need to be included to prevent replaceMerge from removing them
  const chartOptionSeries = (chartOptions?.series as SeriesOption[]) ?? [];
  const typeWellUpdates = chartOptionSeries
    .filter((s) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - kind is a custom property
      return s.kind === "typewell";
    })
    .map((s) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const { id, originalId, symbolSize } = s;
      const isHovered = hoverLegendId === originalId;
      return {
        id,
        itemStyle: {
          opacity: isOpaque ? DEFAULT_OPACITY : isHovered ? DEFAULT_OPACITY : BLUR_OPACITY
        },
        symbolSize: symbolSize ?? 30,
        z: isHovered ? HIGHLIGHTED_Z_INDEX : TYPEWELL_Z_INDEX + 1
      };
    });

  return [...highlightedSeries, ...typeWellUpdates];
}
